import type { Device, Mode, Phase, Room, Routine, State, Trigger, Weekday } from '../api/types';
import { WEEKDAYS } from '../api/types';
import { parseHHMM } from './time';

export const isSensor = (d: Device) => d.type === 'capteur-temperature';
export const isRadiator = (d: Device) => d.type === '4-ordres-2-relais';
export const deviceName = (d: Pick<Device, 'name' | 'client_id'> | undefined | null) => (d ? d.name || d.client_id : '—');

export function byId<T extends { id: string }>(items: T[] | undefined): Map<string, T> {
  return new Map((items ?? []).map((x) => [x.id, x]));
}

export function roomName(rooms: Map<string, Room>, id: string | null | undefined) {
  if (!id) return '—';
  return rooms.get(id)?.name ?? id;
}

export interface RoomGroup {
  room: Room | { id: string; name: string };
  devices: Device[];
}

/** Regroupe les devices par pièce, pièces vides comprises, « Non assignée » en dernier. */
export function groupByRoom(devices: Device[], rooms: Room[]): RoomGroup[] {
  const groups = new Map<string, RoomGroup>();
  for (const r of rooms) groups.set(r.id, { room: r, devices: [] });
  for (const d of devices) {
    if (!groups.has(d.room_id)) groups.set(d.room_id, { room: { id: d.room_id, name: d.room_id }, devices: [] });
    groups.get(d.room_id)!.devices.push(d);
  }
  return [...groups.values()].sort((a, b) => Number(!!(a.room as Room).reserved) - Number(!!(b.room as Room).reserved));
}

export const stateFor = (states: State[] | undefined, deviceId: string) => states?.find((s) => s.device_id === deviceId);

export function triggerOf(p: Pick<Phase, 'start_trigger'>): Trigger {
  const t = p.start_trigger as Partial<Trigger> & { type?: string };
  if (t?.type === 'reveil') {
    const w = t as Partial<{ preheat_minutes: number; duration_minutes: number }>;
    return { type: 'reveil', preheat_minutes: w.preheat_minutes ?? 45, duration_minutes: w.duration_minutes ?? 45 };
  }
  return { type: 'heure_fixe', time: (t as { time?: string })?.time ?? '00:00' };
}

export interface Segment {
  start: number;
  end: number;
  mode: Mode;
  label: string;
  carried: boolean;
}

/**
 * Prévision locale d'une journée type (REQ-15 refusée côté API) : chaque phase active démarre à son heure,
 * la précédente comble l'attente. Le réveil est placé à `wakeAt` s'il est fourni, sinon sauté.
 * Ne tient compte ni de la présence, ni des forçages, absences ou mesures.
 */
export function forecastDay(phases: Phase[], wakeAt: number | null): Segment[] {
  const starts: { start: number; mode: Mode; label: string }[] = [];
  for (const p of phases) {
    if (!p.active) continue;
    const t = triggerOf(p);
    if (t.type === 'heure_fixe') {
      const m = parseHHMM(t.time);
      if (m !== null) starts.push({ start: m, mode: p.mode, label: p.name || t.time });
    } else if (wakeAt !== null) {
      starts.push({ start: Math.max(0, wakeAt - t.preheat_minutes), mode: p.mode, label: p.name || 'Réveil' });
    }
  }
  starts.sort((a, b) => a.start - b.start);
  if (!starts.length) return [];
  const last = starts[starts.length - 1];
  const points = starts[0].start > 0 ? [{ start: 0, mode: last.mode, label: last.label, carried: true }, ...starts.map((s) => ({ ...s, carried: false }))] : starts.map((s) => ({ ...s, carried: false }));
  return points.map((p, i) => ({ ...p, end: i + 1 < points.length ? points[i + 1].start : 1440 })).filter((s) => s.end > s.start);
}

export interface CoverageCell {
  routine: Routine | null;
  source: 'device' | 'room' | null;
}

/** Routine suivie par un radiateur pour chaque jour (§4 « Assignation de routine »). */
export function coverage(device: Device, routines: Routine[]): CoverageCell[] {
  const active = routines.filter((r) => r.active);
  const covers = (r: Routine, d: Weekday) => r.weekdays.includes(d);
  return WEEKDAYS.map((day) => {
    const own = active.find((r) => covers(r, day) && r.assignments?.some((a) => a.target_type === 'device' && a.target_id === device.id));
    if (own) return { routine: own, source: 'device' as const };
    const room = active.find((r) => covers(r, day) && r.assignments?.some((a) => a.target_type === 'room' && a.target_id === device.room_id));
    if (room) return { routine: room, source: 'room' as const };
    return { routine: null, source: null };
  });
}

/** Vérifie les 4 combinaisons de relais (toutes distinctes, valeurs 0/1). */
export function mappingValid(m: Record<Mode, number[]>): boolean {
  const keys = Object.values(m).map((v) => v.join(''));
  return keys.length === 4 && new Set(keys).size === 4 && Object.values(m).every((v) => v.length === 2 && v.every((x) => x === 0 || x === 1));
}
